import type { PipeTransform } from '@angular/core'
import { DOCUMENT, Pipe, inject } from '@angular/core'
import type { SafeHtml } from '@angular/platform-browser'
import { DomSanitizer } from '@angular/platform-browser'

import { TranslateService } from '@ngx-translate/core'

import type { ArticleFootnote } from '../../api/responses'
import type { SafeHtmlObject } from '../utils/html-utils'
import { escapeHtmlSpecialChars, isSafeHtmlObject } from '../utils/html-utils'
import { HtmlToPlaintextPipe } from './html-to-plaintext.pipe'

@Pipe({
  name: 'processFootnotes',
  pure: false,
})
/**
 * Adds the content of footnotes into the content of the article as a popup
 * tooltip. The content of the reference will be flattened to plain text.
 *
 * Usage: {{ content | htmlContent | processContent | processFootnotes:
 * footnotes }}
 *
 * Note: This pipe is marked as impure (pure: false) to allow updates when the
 * input content changes.
 */
export class ProcessFootnotesPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer)
  private document = inject<Document>(DOCUMENT)
  private translateService = inject(TranslateService)

  private plainTextPipe: HtmlToPlaintextPipe

  // Cache the last result so unchanged content doesn't produce a new SafeHtml
  // reference every change-detection cycle. A new reference forces Angular to
  // rewrite the bound innerHTML, which clears any active text selection.
  private lastContentString?: string
  private lastFootnotes?: ArticleFootnote[]
  private lastResult?: SafeHtml

  constructor() {
    this.plainTextPipe = new HtmlToPlaintextPipe()
  }

  transform(content: SafeHtml, footnotes?: ArticleFootnote[]): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('')
    }

    try {
      const contentString: string = (content as SafeHtmlObject)
        .changingThisBreaksApplicationSecurity

      if (
        this.lastResult !== undefined &&
        contentString === this.lastContentString &&
        footnotes === this.lastFootnotes
      ) {
        return this.lastResult
      }

      let result: SafeHtml
      if (!footnotes || footnotes.length === 0) {
        result = this.sanitizer.bypassSecurityTrustHtml(contentString)
      } else {
        // Match [number] footnote pattern
        const processedContent = contentString.replace(
          /\[(\d+)\]/g,
          (match: string, number: string) => {
            return this.insertFootnoteAnchor(footnotes, match, number)
          }
        )

        // Match <sup class="footnote-number">number</sup> footnote pattern
        const processedContent2 = processedContent.replace(
          /<sup class="footnote-number"[^>]*>(\d+)<\/sup>/g,
          (match: string, number: string) => {
            return this.insertFootnoteAnchor(footnotes, match, number)
          }
        )

        result = this.sanitizer.bypassSecurityTrustHtml(processedContent2)
      }

      this.lastContentString = contentString
      this.lastFootnotes = footnotes
      this.lastResult = result
      return result
    } catch (error) {
      console.error('Error in processFootnotes pipe:', error)
      return isSafeHtmlObject(content)
        ? content
        : this.sanitizer.bypassSecurityTrustHtml(String(content))
    }
  }

  private insertFootnoteAnchor = (
    footnotes: ArticleFootnote[],
    match: string,
    number: string
  ): string => {
    const footnote = footnotes[Number(number) - 1]
    if (!footnote) {
      console.warn(`No footnote found for number: ${number}`)
      return match // If no footnote found, return original match
    }
    const translatedLabel = this.translateService.instant(
      'pages.articles.footnoteReference',
      { number: number }
    )
    // We use comments to prevent adding whitespace between elements
    return `<a class="footnote-tooltip" data-footnote="${number}" href="#footnote-${number}" aria-label="${translatedLabel}"><!--
      --><sup id="footnote-ref-${number}"><span>${number}</span></sup><!--
      --><span class="footnote-tooltip-content">${escapeHtmlSpecialChars(this.plainTextPipe.transform(footnote.content))}</span><!--
    --></a>`
  }
}
